import type { Experience, Membership, Permission, Role, WorkspaceKind } from '@buakei/types';

/**
 * Role → permission map. Note what is ABSENT: no administrative role grants `matter.read`.
 * Matter access only ever comes from an explicit matter grant (SEC-002).
 */
export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  lawyer: ['research.use', 'drafting.use', 'documents.read', 'matter.read'],
  firm_admin: ['firm.members.manage'],
  student: ['learning.use'],
  legal_editor: ['editorial.view', 'editorial.review'],
  legal_reviewer: ['editorial.view', 'editorial.review', 'editorial.approve'],
  support_agent: ['operations.view'],
  platform_admin: ['operations.view'],
};

export const ROLE_EXPERIENCE: Readonly<Record<Role, Experience>> = {
  lawyer: 'professional',
  firm_admin: 'professional',
  student: 'learning',
  legal_editor: 'editorial',
  legal_reviewer: 'editorial',
  support_agent: 'operations',
  platform_admin: 'operations',
};

export function permissionsForRoles(roles: readonly Role[]): Permission[] {
  return [...new Set(roles.flatMap((role) => ROLE_PERMISSIONS[role]))];
}

/** Experiences available to an account, in a stable display order. */
export function experiencesForMemberships(memberships: readonly Membership[]): Experience[] {
  const order: Experience[] = ['professional', 'learning', 'editorial', 'operations'];
  const held = new Set(memberships.flatMap((m) => m.roles.map((role) => ROLE_EXPERIENCE[role])));
  return order.filter((experience) => held.has(experience));
}

/** The operations console a platform administrator's membership refers to (ADR-008). */
export const OPERATIONS_CONSOLE_ID = 'console_operations';

/**
 * The Editorial console (ADR-023): legal editors and reviewers of the public legal corpus. A
 * platform administrator grants it like a workspace, but it holds no data and never any
 * matter, firm or personal content.
 */
export const EDITORIAL_CONSOLE_ID = 'console_editorial';
export const EDITORIAL_ROLES: readonly Role[] = ['legal_editor', 'legal_reviewer'];

export function rolesGrantableInEditorialConsole(roles: readonly Role[]): boolean {
  return roles.length > 0 && roles.every((role) => EDITORIAL_ROLES.includes(role));
}

/**
 * Roles an administrator may grant in each workspace kind (ADR-008). `platform_admin` is never
 * grantable: it comes only from server configuration. Editorial and support roles belong to
 * consoles, which are not data workspaces.
 */
export const GRANTABLE_ROLES: Readonly<Record<WorkspaceKind, readonly Role[]>> = {
  personal: ['lawyer'],
  firm: ['lawyer', 'firm_admin'],
  learning: ['student'],
};

export function rolesGrantableIn(kind: WorkspaceKind, roles: readonly Role[]): boolean {
  return roles.length > 0 && roles.every((role) => GRANTABLE_ROLES[kind].includes(role));
}
